import {
  ConversationChannelType,
  ConversationParticipantEntity,
} from '@provision/conversation-types';
import { ReactNode, useMemo } from 'react';
import {
  SelectMenu,
  SelectMenuOption,
  SelectMenuTriggerState,
} from 'src/components/SelectMenu';
import { useClientEmails } from 'src/graphql/entities/client-email';
import { ParticipantSelectorItem } from 'src/pages/Conversations/Chat/ParticipationPanel/types';

interface ParticipantChipMenuProps {
  participant: ParticipantSelectorItem;
  onAddEmail: (data: ParticipantSelectorItem, email: string) => void;
  onRemoveEmail: (data: ParticipantSelectorItem, email: string) => void;
  renderTrigger: (state: SelectMenuTriggerState) => ReactNode;
}

export const ParticipantChipMenu = ({
  participant,
  onAddEmail,
  onRemoveEmail,
  renderTrigger,
}: ParticipantChipMenuProps) => {
  const { clientEmails } = useClientEmails({
    clientId:
      participant.participantEntity === ConversationParticipantEntity.Client
        ? participant.participantIdentity
        : '',
  });

  const preparedEmails = useMemo(() => {
    const result = new Set(clientEmails.map((record) => record.value));

    participant.channels.forEach((channel) => {
      if (
        (channel.conversationChannelTypeId as ConversationChannelType) ===
        ConversationChannelType.Email
      ) {
        result.add(channel.externalIdentifier);
      }
    });

    return Array.from(result);
  }, [clientEmails, participant.channels]);

  const options = useMemo<SelectMenuOption[]>(
    () => preparedEmails.map((email) => ({ value: email, title: email, group: 'Emails' })),
    [preparedEmails],
  );

  const selectedEmails = useMemo(
    () =>
      preparedEmails.filter((email) =>
        participant.channels.some((channel) => channel.externalIdentifier === email),
      ),
    [preparedEmails, participant.channels],
  );

  const handleEmailToggle = (email: string) => {
    if (selectedEmails.includes(email)) {
      onRemoveEmail(participant, email);
      return;
    }
    onAddEmail(participant, email);
  };

  return (
    <SelectMenu
      label="Emails"
      multiple
      sectioned
      options={options}
      values={selectedEmails}
      onValuesChange={(_, option) => handleEmailToggle(option.value)}
      menuWidth="20rem"
      renderTrigger={renderTrigger}
    />
  );
};
